<template>
  <Render />
</template>

<script setup lang="ts">
import { pascalCase } from 'scule'
import type { Schemas } from '#shopware'

const route = useRoute()
const { locale, locales, defaultLocale } = useI18n()
const { getShopRoute } = useShopRouter()
const { resolvePath } = useNavigationSearch()
const { shopPath } = useAlternates()
const { apiClient } = useShopwareContext()
const { changeLanguage } = useInternationalization()

const shopComponents = {
  ShopFrontendNavigationPage: resolveComponent(
    'LazyShopFrontendNavigationPage'
  ),
  ShopFrontendDetailPage: resolveComponent('LazyShopFrontendDetailPage'),
}

// Helper function to resolve SEO path with optional trailing slash
const resolveSeoPath = async (
  path: string,
  withTrailingSlash: boolean = true
) => {
  if (withTrailingSlash && !path.endsWith('/')) {
    path += '/'
  } else if (!withTrailingSlash && path.endsWith('/')) {
    path = path.slice(0, -1)
  }
  const normalizedRoutePath = path === '' ? '/' : path
  return await resolvePath(normalizedRoutePath)
}

const urlLocale = route.path.match(/^\/([a-z]{2})\//)?.[1] || defaultLocale
let routePath = route.path
  .replace(`/${locale.value}/`, '/')
  .replace(/^\/shop(?:\/|$)/, '/')
const routePrefix = `/${useUrlResolver().getUrlPrefix()}/`
if (routePath.startsWith(routePrefix)) {
  routePath = `/${routePath.substring(routePrefix.length)}`
}

/*
  set locale
  especially if nacigating back or swichtignb langs quickly
*/
const urlProps = locales.value.find((l) => l.code === urlLocale)!
// ignore on technical urls as they don't have langauge codes
if (
  !routePath.startsWith('/navigation/') &&
  !routePath.startsWith('/detail/')
) {
  if (urlProps?.code !== locale.value) {
    locale.value = urlProps.code
    changeLanguage(urlProps.languageId)
  }
  apiClient.defaultHeaders.apply({ 'sw-language-id': urlProps.languageId })
}

/*
  fetch shopware seo info
*/
const { data: seoResult } = await useAsyncData(
  'cmsResponse:' + route.path,
  async () => {
    // For client links if the history state contains seo url information we can omit the api call
    if (import.meta.client) {
      if (history.state?.routeName) {
        return {
          routeName: history.state?.routeName,
          foreignKey: history.state?.foreignKey,
        }
      }
    }
    // Ensure routePath ends without a slash because SEO URLS are stored without a trailing slash
    // https://jkwebch.atlassian.net/browse/JPR-597
    let result = await resolveSeoPath(routePath, false)

    // As a fallback, just in case we can't find the seoUrl with a trailing slash, try without slash
    if (!result) {
      result = await resolveSeoPath(routePath, true)
    }

    return result
  }
)

if (!seoResult.value) {
  throw createError({
    message: `failed to resolve route for path "${routePath}" for language "${urlProps!.code}"`,
    statusCode: 404,
    fatal: true,
  })
}

// if the url is not human readable we redirect
const seoPath = seoResult.value.seoPathInfo
if (
  seoPath &&
  (routePath.startsWith('/navigation/') || routePath.startsWith('/detail/'))
) {
  await navigateTo(getShopRoute(seoPath), {
    replace: true,
    redirectCode: 301,
  })
}

const { routeName, foreignKey } = useNavigationContext(
  seoResult as Ref<Schemas['SeoUrl']>
)

function Render() {
  const componentName = pascalCase('shop.' + routeName.value)
  const component = Object.prototype.hasOwnProperty.call(
    shopComponents,
    componentName
  )
    ? shopComponents[componentName as keyof typeof shopComponents]
    : undefined

  if (component) {
    return h(
      'div',
      h(component, { navigationId: foreignKey.value, key: routePath })
    )
  }

  throw createError({
    message: `Component ${componentName} not found`,
    statusCode: 500,
    fatal: true,
  })
}

onMounted(() => {
  shopPath.value = seoResult.value.pathInfo
})

onBeforeUnmount(() => {
  shopPath.value = null
})
</script>
